import { from, empty, interval, merge, of } from 'rxjs'
import { catchError, map, startWith, switchMap } from 'rxjs/operators'
import { ofType } from 'redux-observable'
import { queryFromString } from 'helpers/url'
import { fetchVersion } from 'api/planet/version'
import { fetchResource } from 'api/planet/resource'
import { isProductPagePath } from 'domain/router'
import { captureException } from 'helpers/raven'
import * as Actions from 'store/actions/index.ts'
import { getIsPreviewMode, getLocation, getCountryCode, getCultureCode, getSaleKey, getVersion, getMarketId, getShouldDoubleThePriceFeatureEnabled } from '../selectors'
import { handleUnauthorized } from './helpers'

export const periodicalVersionCheckEpic = (actions$, state$) => actions$.pipe(
  ofType(
    Actions.member.MEMBER_INFO_FETCHED,
    Actions.version.NEW_VERSION
  ),
  switchMap(() =>
    interval(5 * 60 * 1000).pipe(
      switchMap(
        () => fetchVersion(state$.value).pipe(
          switchMap(({ response: version }) => {
            const currentVersion = getVersion(state$.value)

            if (currentVersion !== version) {
              return from([
                Actions.version.onVersionChanged(),
                Actions.version.onNewVersion(version)
              ])
            }
            return of(Actions.fetch.onFetchCompleted())
          }),
          startWith(Actions.fetch.onFetchStarted()),
          catchError(versionFetchError => {
            if (versionFetchError.status === 401) { return handleUnauthorized(actions$, state$) }

            captureException(new Error('Unable to periodically fetch version'), {
              extra: { versionFetchError },
              level: 'warning'
            })
            return of(Actions.fetch.onFetchCompleted())
          })
        )
      )
    )
  )
)

const handleSimpleFetchResource = (
  type, saleKey, key, fetchObservable
) => fetchObservable.pipe(
  map(({ response }) => Actions.resources.onResourceFetched(type, saleKey, key, response)),
  startWith(Actions.fetch.onFetchStarted()),
  catchError(resourceFetchError => {
    captureException(new Error('Unable to fetch resource after version change'), {
      extra: { resourceFetchError }
    })
    return of(Actions.resources.onResourceFetchError(resourceFetchError, type, key))
  })
)

export const refetchAfterVersionChangeEpic = (actions$, state$) => actions$.pipe(
  ofType(Actions.version.NEW_VERSION),
  switchMap(() => {
    const state = state$.value

    const countryCode = getCountryCode(state)
    const cultureCode = getCultureCode(state)
    const isPreview = getIsPreviewMode(state)
    const { pathname, search } = getLocation(state)
    const marketId = getMarketId(state)
    const saleKey = getSaleKey(state)
    const saleVersion = getVersion(state)
    const isResourceV2 = getShouldDoubleThePriceFeatureEnabled(state)

    if (!countryCode || !cultureCode || !saleVersion) return empty()

    const { key } = queryFromString(search)

    const saleFetch = handleSimpleFetchResource(
      'sale',
      saleKey,
      saleKey,
      fetchResource(saleKey, saleKey, countryCode, cultureCode, marketId, saleVersion, isPreview, isResourceV2)
    )

    const currentResourceFetch = isProductPagePath(pathname)
      ? handleSimpleFetchResource(
        'product',
        saleKey,
        key,
        fetchResource(saleKey, key, countryCode, cultureCode, marketId, saleVersion, isPreview, isResourceV2)
      )
      : empty()

    return merge(saleFetch, currentResourceFetch)
  })
)
